import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Autocomplete,
    TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import mapboxgl from 'mapbox-gl';
import clustersDbscan from '@turf/clusters-dbscan';
import { point, featureCollection } from '@turf/helpers';
import axios from 'axios';

export const ProductLocationPopup = ({ open, onClose, product, website, category_images }) => {
    const [locationData, setLocationData] = useState(null);
    const [staticInformation, setStaticInformation] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const mapContainer = useRef(null);
    const map = useRef(null);
    const [mapInitialized, setMapInitialized] = useState(false);
    const [expandedPanel, setExpandedPanel] = useState('panel1'); // Default to first panel
    const [selectedVariations, setSelectedVariations] = useState([]);
    
    // Get all available variations from location data - only include variations that exist and have prices
    const getAvailableVariations = () => {
        if (!locationData || locationData.length === 0) return [];
        
        const allVariations = new Set();
        locationData.forEach(location => {
            if (location.variations && typeof location.variations === 'object') {
                Object.keys(location.variations).forEach(variationName => {
                    const price = location.variations[variationName];
                    // Only add if variation has a price (not null/undefined) and name is not just a number
                    if (price) {
                        allVariations.add(variationName);
                    }
                });
            }
        });
        
        return Array.from(allVariations);
    };
    
    // Initialize selected variations when location data changes
    useEffect(() => {
        if (locationData && locationData.length > 0) {
            const allVariations = getAvailableVariations();
            setSelectedVariations(allVariations); // Start with all selected
        }
    }, [locationData]);
    
    // Filter location data by selected variations
    const getFilteredLocationData = () => {
        if (!locationData || selectedVariations.length === 0) return [];
        
        return locationData.map(location => ({
            ...location,
            // Add variation info to each location based on location variations and selected filters
            availableVariations: selectedVariations.filter(variation => 
                location?.variations?.[variation] !== null && location?.variations?.[variation] !== undefined
            )
        }));
    };

    useEffect(() => {
        if (open && product) {
            fetchLocationData();
        }
    }, [open, product]);

    useEffect(() => {
        if (open && locationData && locationData.length > 0 && mapContainer.current && !mapInitialized && expandedPanel === 'panel2') {
            initializeMap();
        }
    }, [open, locationData, mapInitialized, expandedPanel]);

    useEffect(() => {
        if (!open && map.current) {
            map.current.remove();
            map.current = null;
            setMapInitialized(false);
        }
    }, [open]);

    const fetchLocationData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post('/gettheurl/product-location-level', {
                product: product,
                website,
            });

            console.log('Location data received: ', response.data)
            console.log('First location sample: ', response.data[0])
            setLocationData(response.data);
            // setStaticInformation(response.data)
        } catch (err) {
            setError('Failed to fetch location data');
            console.error('Error fetching location data:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (priceData) => {
        if (!priceData) return 'Price not available';
        
        // Handle clean_price object format
        if (typeof priceData === 'object') {
            if (priceData.min && priceData.max) {
                return `$${priceData.min} - $${priceData.max}`;
            } else if (priceData.price) {
                console.log('priceData: ', priceData)
                return `$${priceData.price}`;
            }
        }
        
        // Handle legacy format
        return typeof priceData === 'number' ? `$${priceData}` : priceData;
    };

    const initializeMap = () => {
        if (!mapContainer.current || mapInitialized || !locationData) return;

        // Set Mapbox access token
        mapboxgl.accessToken = 'pk.eyJ1Ijoic25pa2FlaW4iLCJhIjoiY2s1eWNpcWxrMDJvbzNtcDdyb3puNTVrMCJ9.t-VFWsbpRo--tRSLgLa9ng';

        // Filter valid coordinates
        const validLocations = locationData.filter(item => 
            item && 
            item.coordinates && 
            Array.isArray(item.coordinates) &&
            item.coordinates.length === 2 &&
            !isNaN(item.coordinates[0]) &&
            !isNaN(item.coordinates[1])
        );

        if (validLocations.length === 0) return;

        // Calculate center of all points
        const avgLng = validLocations.reduce((sum, item) => sum + item.coordinates[0], 0) / validLocations.length;
        const avgLat = validLocations.reduce((sum, item) => sum + item.coordinates[1], 0) / validLocations.length;

        // Initialize map
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/light-v11',
            center: [avgLng, avgLat],
            zoom: 10
        });

        map.current.on('load', () => {
            createClusters(validLocations);
            setMapInitialized(true);
        });
    };

    const createClusters = (validLocations) => {
        // Convert your validLocations to GeoJSON
        const geojson = {
            type: 'FeatureCollection',
            features: validLocations.map(item => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: item.coordinates
                },
                properties: {
                    price: item.clean_price?.price || item.clean_price?.min || 0
                }
            }))
        };
        console.log('geojson: ', geojson)
        // Add source with clusterProperties to calculate sum of prices and count
        map.current.addSource('locations', {
            type: 'geojson',
            data: geojson,
            cluster: true,
            clusterRadius: 50, // adjust for your desired clustering sensitivity
            clusterProperties: {
                sum_price: ["+", ["get", "price"]],
                point_count: ["+", 1]
            }
        });
    
        // Layer for cluster circles
        map.current.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'locations',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': '#51bbd6',
                'circle-radius': 20
            }
        });
    
        // Layer for cluster labels showing average price
        map.current.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'locations',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': [
                    "concat",
                    "$",
                    [
                        "to-string",
                        [
                            "/",
                            ["round", ["*", ["/", ["get", "sum_price"], ["get", "point_count"]], 100]],
                            100
                        ]
                    ]
                ],
                'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
                'text-size': 18
            },
            paint: {
                'text-color': '#ffffff',
                'text-halo-color': '#000000',
                'text-halo-width': 1
            }
        });
    
        // Layer for unclustered points
        map.current.addLayer({
            id: 'unclustered-label',
            type: 'symbol',
            source: 'locations',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'text-field': [
                    "concat",
                    "$",
                    [
                        "to-string",
                        [
                            "/",
                            ["round", ["*", ["get", "price"], 100]],
                            100
                        ]
                    ]
                ],
                'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
                'text-size': 16
            },
            paint: {
                'text-color': '#ffffff',
                'text-halo-color': '#000000',
                'text-halo-width': 1
            }
        });
    };
    

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minHeight: '400px'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="div">
                    {product?.name || 'Product Details'}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {product && (
                    <Box sx={{ mb: 3 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    {category_images[product.clean_category] ? (
                                        <img
                                            src={`https://maptize.s3.amazonaws.com/food_categories/${category_images[product.clean_category]}`}
                                            alt={product.name || 'Product'}
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '200px',
                                                objectFit: 'cover',
                                                borderRadius: '8px'
                                            }}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://i.ibb.co/8NYd9pw/white-background.png";
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                height: 200,
                                                backgroundColor: '#f5f5f5',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 2
                                            }}
                                        >
                                            <Typography color="text.secondary">No Image</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Typography variant="h5" gutterBottom>
                                    {product.name || 'Product Name Not Available'}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    Category: {product.category || 'Category Not Available'}
                                </Typography>
                                {product.calories && (<Typography variant="h6" color="primary" gutterBottom>
                                    Calories: {product.calories}
                                </Typography>)}
                                {product.quantity && (
                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                    Quantity: {product.quantity}
                                    </Typography>
                                )}
                                {product.description && (
                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                        {product.description}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Expandable Sections */}
                <Box sx={{ mt: 3 }}>
                    {/* Variations by Location Accordion */}
                    <Accordion 
                        expanded={expandedPanel === 'panel1'} 
                        onChange={() => setExpandedPanel(expandedPanel === 'panel1' ? false : 'panel1')}
                        sx={{ mb: 2 }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                        >
                            <Typography variant="h6">Variations by Location</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {loading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                    <Typography sx={{ ml: 2 }}>Loading location data...</Typography>
                                </Box>
                            )}

                            {error && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography color="error">{error}</Typography>
                                    <Button onClick={fetchLocationData} sx={{ mt: 1 }}>
                                        Retry
                                    </Button>
                                </Box>
                            )}

                            {locationData && !loading && getAvailableVariations().length > 0 && (
                                <Box>
                                    {/* Variation Filter */}
                                    <Box sx={{ mb: 3 }}>
                                        <Autocomplete
                                            multiple
                                            options={getAvailableVariations()}
                                            value={selectedVariations}
                                            onChange={(event, newValue) => {
                                                setSelectedVariations(newValue);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Filter by Variations"
                                                    placeholder="Select variations to display"
                                                />
                                            )}
                                            sx={{ width: '100%' }}
                                        />
                                    </Box>

                                    {/* Locations with Variations */}
                                    {locationData.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {locationData.map((item, index) => (
                                                <Card key={index} variant="outlined" sx={{ p: 2 }}>
                                                    <Grid container spacing={2} alignItems="flex-start">
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="subtitle1" fontWeight="bold">
                                                                {item.address || 'Address not available'}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {item.county && item.state ? `${item.county}, ${item.state}` : 'Location not available'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                                Available Variations:
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                {selectedVariations
                                                                    .filter(variation => {
                                                                        const price = item.variations?.[variation];
                                                                        return price !== null && price !== undefined;
                                                                    })
                                                                    .map((variation, vIndex) => {
                                                                    const price = item.variations[variation];
                                                                    
                                                                    return (
                                                                        <Box 
                                                                            key={vIndex}
                                                                            sx={{ 
                                                                                display: 'flex', 
                                                                                justifyContent: 'space-between', 
                                                                                alignItems: 'center',
                                                                                p: 1,
                                                                                backgroundColor: '#f0f7ff',
                                                                                borderRadius: 1,
                                                                                border: '1px solid #e3f2fd'
                                                                            }}
                                                                        >
                                                                            <Typography 
                                                                                variant="body2" 
                                                                                sx={{ 
                                                                                    fontWeight: 500,
                                                                                    color: 'primary.main'
                                                                                }}
                                                                            >
                                                                                {variation}
                                                                            </Typography>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <Typography 
                                                                                    variant="body2" 
                                                                                    color="primary"
                                                                                    sx={{ fontWeight: 600 }}
                                                                                >
                                                                                    {formatPrice(price)}
                                                                                </Typography>
                                                                                <Box 
                                                                                    sx={{ 
                                                                                        width: 8,
                                                                                        height: 8,
                                                                                        borderRadius: '50%',
                                                                                        backgroundColor: '#4caf50'
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Card>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography color="text.secondary">
                                                No location data available
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* Price Distribution Map Accordion */}
                    {locationData && locationData.length > 0 && (
                        <Accordion 
                            expanded={expandedPanel === 'panel2'} 
                            onChange={() => setExpandedPanel(expandedPanel === 'panel2' ? false : 'panel2')}
                            sx={{ mb: 2 }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel2-content"
                                id="panel2-header"
                            >
                                <Typography variant="h6">Price Distribution Map</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box 
                                    ref={mapContainer} 
                                    sx={{ 
                                        height: '400px', 
                                        width: '100%', 
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0'
                                    }} 
                                />
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Price Distribution by Location Accordion */}
                    {locationData && locationData.length > 0 && (
                        <Accordion 
                            expanded={expandedPanel === 'panel3'} 
                            onChange={() => setExpandedPanel(expandedPanel === 'panel3' ? false : 'panel3')}
                            sx={{ mb: 2 }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel3-content"
                                id="panel3-header"
                            >
                                <Typography variant="h6">Price Distribution by Location</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {locationData
                                        .sort((a, b) => {
                                            const priceA = a.clean_price?.price || a.clean_price?.min || 0;
                                            const priceB = b.clean_price?.price || b.clean_price?.min || 0;
                                            return priceB - priceA;
                                        })
                                        .map((item, index) => (
                                        <Card key={index} variant="outlined" sx={{ p: 2 }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {item.address || 'Address not available'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.county && item.state ? `${item.county}, ${item.state}` : 'Location not available'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} md={3}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="h6" color="primary" fontWeight="bold">
                                                            {item.clean_price ? formatPrice(item.clean_price) : 'N/A'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Price
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6} md={3}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            #{index + 1}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Rank
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Card>
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};